import { toast } from "sonner";

export const CATEGORY_LABELS: Record<string, string> = {
  // MVA and Engagement letter uploaded to a prospect move its stage automatically.
  mva: "MVA",
  engagement_letter: "Engagement letter",
  teaser: "Teaser",
  cim: "CIM",
  nda: "NDA",
  financials: "Financials",
  legal: "Legal",
  contract: "Contract",
  report: "Report",
  other: "Other",
};

export const CATEGORY_COLORS: Record<string, string> = {
  mva: "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400",
  engagement_letter: "bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400",
  teaser: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400",
  cim: "bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400",
  nda: "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400",
  financials: "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400",
  legal: "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400",
  contract: "bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-400",
  report: "bg-cyan-100 text-cyan-700 dark:bg-cyan-900/30 dark:text-cyan-400",
  other: "bg-gray-100 text-gray-700 dark:bg-gray-900/30 dark:text-gray-400",
};

export const formatFileSize = (bytes: number | null) => {
  if (!bytes) return "Unknown size";
  const kb = bytes / 1024;
  if (kb < 1024) return `${Math.round(kb)} KB`;
  const mb = kb / 1024;
  return `${Math.round(mb * 10) / 10} MB`;
};

export type DocumentOpen = "stored" | "inline" | "missing";

/**
 * /objects/ paths live in Supabase Storage. data: URLs are what the old buyer
 * sidebar uploaded, with the bytes in the row itself, so they still open.
 * Anything else points at a store that is gone.
 */
export function documentOpenTarget(fileUrl: string | null | undefined): DocumentOpen {
  if (fileUrl?.startsWith("/objects/")) return "stored";
  if (fileUrl?.startsWith("data:")) return "inline";
  return "missing";
}

export function decodeDataUrl(dataUrl: string): { mime: string; bytes: Uint8Array } | null {
  const comma = dataUrl.indexOf(",");
  if (comma < 0) return null;
  const head = dataUrl.slice(0, comma);
  if (!/;base64$/i.test(head)) return null;
  const mime = /^data:([^;,]+)/i.exec(head)?.[1] ?? "application/octet-stream";
  try {
    const bin = atob(dataUrl.slice(comma + 1));
    return { mime, bytes: Uint8Array.from(bin, (ch) => ch.charCodeAt(0)) };
  } catch {
    return null;
  }
}

const INLINE_SAFE = new Set([
  "application/pdf", "image/png", "image/jpeg", "image/gif", "image/webp", "text/plain", "text/csv",
]);

/** Only inert types may open as a page on our origin; html, svg and the rest would run script. */
export function canOpenInline(mime: string): boolean {
  return INLINE_SAFE.has(mime.split(";")[0].trim().toLowerCase());
}

/** Open a document in a new tab, or download it when its type is not safe to open here. */
export function openDocument(fileUrl: string | null | undefined, fileName = "document"): void {
  const how = documentOpenTarget(fileUrl);
  if (how === "stored") { window.open(fileUrl!, "_blank", "noopener"); return; }
  if (how !== "inline") { toast.error("Couldn't open this file"); return; }
  const decoded = decodeDataUrl(fileUrl!);
  if (!decoded) { toast.error("Couldn't open this file"); return; }
  const inline = canOpenInline(decoded.mime);
  const url = URL.createObjectURL(new Blob([decoded.bytes], { type: inline ? decoded.mime : "application/octet-stream" }));
  if (inline) {
    window.open(url, "_blank", "noopener");
  } else {
    const a = document.createElement("a");
    a.href = url;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    a.remove();
  }
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}
